import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import Modal from '@/components/common/Modal';

interface Provider {
  id: string;
  name: string;
}

interface Expense {
  id: string;
  amount: number;
  provider_id: string;
  provider: Provider;
  date: string;
  document_url: string | null;
}

interface ExpenseDetailsModalProps {
  expense: Expense | null;
  onClose: () => void;
  onDocumentDelete?: () => void;
}

export default function ExpenseDetailsModal({ expense, onClose, onDocumentDelete }: ExpenseDetailsModalProps) {
  const [documentData, setDocumentData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteDocModalOpen, setIsDeleteDocModalOpen] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    if (expense?.document_url) {
      fetchDocument();
    }
  }, [expense]);

  const fetchDocument = async () => {
    if (!expense?.document_url) return;

    setLoading(true);
    setError(null);

    try {
      const filePath = expense.document_url.split('nestor/')[1];
      const { data, error } = await supabase.storage
        .from('nestor')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      setDocumentData(url);
    } catch (err) {
      console.error('Error fetching document:', err);
      setError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!expense?.document_url) return;

    try {
      const filePath = expense.document_url.split('nestor/')[1];
      const { data, error } = await supabase.storage
        .from('nestor')
        .download(filePath);

      if (error) throw error;

      // Create a download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addToast({
        type: 'success',
        message: 'Document downloaded successfully',
      });
    } catch (err) {
      console.error('Error downloading document:', err);
      addToast({
        type: 'error',
        message: 'Failed to download document',
      });
    }
  };

  const handleDeleteDocument = async () => {
    if (!expense?.document_url) return;

    try {
      const filePath = expense.document_url.split('nestor/')[1];
      const { error: storageError } = await supabase.storage
        .from('nestor')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Update the expense record to remove the document_url
      const { error: dbError } = await supabase
        .from('expenses')
        .update({ document_url: null })
        .eq('id', expense.id);

      if (dbError) throw dbError;

      addToast({
        type: 'success',
        message: 'Document deleted successfully',
      });

      if (onDocumentDelete) {
        onDocumentDelete();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting document:', err);
      addToast({
        type: 'error',
        message: 'Failed to delete document',
      });
    } finally {
      setIsDeleteDocModalOpen(false);
    }
  };

  const isImage = documentData?.startsWith('blob:') && expense?.document_url?.match(/\.(jpg|jpeg|png|gif)$/i);

  return (
    <>
      <Transition.Root show={!!expense} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
          <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="inline-block w-full max-w-4xl transform overflow-hidden rounded-xl bg-white dark:bg-dark-bg-light text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                      Expense Details
                    </Dialog.Title>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary">Provider</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{expense?.provider.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary">Amount</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">€{expense?.amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary">Date</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {expense?.date ? format(new Date(expense.date), 'MMMM d, yyyy') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-dark-text-secondary">Document ID</p>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">{expense?.id}</p>
                    </div>
                  </div>

                  {expense?.document_url && (
                    <div className="border rounded-lg border-gray-200 dark:border-dark-border overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 dark:bg-dark-bg-lighter border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Document Preview</h3>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={handleDownload}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 p-2"
                            title="Download Document"
                          >
                            <i className="fas fa-download" />
                          </button>
                          <button
                            onClick={() => setIsDeleteDocModalOpen(true)}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2"
                            title="Delete Document"
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      </div>
                      <div className="p-4">
                        {loading ? (
                          <div className="flex items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
                          </div>
                        ) : error ? (
                          <div className="flex items-center justify-center h-64 text-red-500 dark:text-red-400">
                            <p>{error}</p>
                          </div>
                        ) : documentData ? (
                          isImage ? (
                            <img
                              src={documentData}
                              alt="Expense document"
                              className="max-w-full h-auto rounded"
                            />
                          ) : (
                            <iframe
                              src={documentData}
                              className="w-full h-[600px] rounded"
                              title="Document preview"
                            />
                          )
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Document Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteDocModalOpen}
        onClose={() => setIsDeleteDocModalOpen(false)}
        title="Delete Document"
        actions={
          <>
            <button
              onClick={() => setIsDeleteDocModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:text-dark-text-primary dark:bg-dark-bg dark:hover:bg-dark-bg-light rounded-lg border border-gray-300 dark:border-dark-border"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteDocument}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Are you sure you want to delete this document? This action cannot be undone.
        </p>
      </Modal>
    </>
  );
} 